import React, { useCallback, useMemo } from 'react';
import { isUndefined } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { ConfirmationModal } from '../../shared/modal/ConfirmationModal';
import styles from './DatasetBuilder.module.scss';
import { getActiveParentDatasetIds, getAllDatasetDefinitions, getIsCheckingForParents, toggleCheckParentDatasetIds, toggleDeleteDatasetConfirmationModal } from './store';
import { PageLoadingDots } from '../../shared/loading-dots/PageLoadingDots';
import { ModalHeader } from '../../shared/modal/ModalHeader';
import { Form } from '../../shared/icons';
import { Scrollable } from '../../shared/scrollable/Scrollable';

interface ActiveParentDatasetsModalProps {
    datasetId: number;
}

export const ActiveParentDatasetsModal: React.FC<ActiveParentDatasetsModalProps> = ({ datasetId }) => {
    const dispatch = useAppDispatch();
    const isChecking = useAppSelector(getIsCheckingForParents);
    const allDefinitions = useAppSelector(getAllDatasetDefinitions);
    const activeParentDatasetIds = useAppSelector(getActiveParentDatasetIds);

    const getDatasetDefinitionByDatasetId = useCallback((selectedDatasetId: number) => allDefinitions.find(({ datasetId }) => datasetId === selectedDatasetId), [allDefinitions]);
    const queriedDatasetDefinition = useMemo(() => getDatasetDefinitionByDatasetId(datasetId), [getDatasetDefinitionByDatasetId, datasetId]);

    const closeModal = useCallback(() => dispatch(toggleCheckParentDatasetIds(null)), [dispatch]);
    const openDeleteConfirmationModal = useCallback(() => !isUndefined(queriedDatasetDefinition) && dispatch(toggleDeleteDatasetConfirmationModal(queriedDatasetDefinition)), [dispatch, queriedDatasetDefinition]);

    const activeParentDefinitions = useMemo(() => activeParentDatasetIds.map(parentDatasetId => getDatasetDefinitionByDatasetId(parentDatasetId)), [activeParentDatasetIds, getDatasetDefinitionByDatasetId]);

    const content = useMemo(() => {
        if (isChecking) {
            return <PageLoadingDots />;
        }
        if (isUndefined(queriedDatasetDefinition)) {
            return null;
        }
        if (activeParentDatasetIds.length === 0) {
            return (
                <div className={styles.noParents}>The selected dataset ({queriedDatasetDefinition.datasetTitle}) does not have any active parent datasets.</div>
            );
        }
        return (
            <>
                <div className={styles.activeParentDatasetsHeader}>The selected dataset ({queriedDatasetDefinition.datasetTitle}) is a child in the following datasets.</div>
                <div className={styles.activeParentDatasets}>
                    <Scrollable>
                        {activeParentDefinitions.map((parentDefinition, index) => (
                            <div key={index} className={styles.parentDefinitionWrapper}>
                                <div className={styles.bulletPoint} />
                                <div className={styles.parentDefinitionTitle}>{parentDefinition?.datasetTitle || ''}</div>
                            </div>
                        ))}
                    </Scrollable>
                </div>
            </>
        );
    }, [isChecking, queriedDatasetDefinition, activeParentDatasetIds, activeParentDefinitions]);

    return (
        <ConfirmationModal
            isOpen
            closeModal={closeModal}
            showConfirm={!isChecking}
            confirmLabel='Delete'
            confirm={openDeleteConfirmationModal}
            closeLabel='Close'
            spaceBetweenButtons
        >
            <div className={styles.activeParentDatasetsModalWrapper}>
                <ModalHeader label='Checking for Active Parent Datasets' icon={Form} />
                <div className={styles.contentWrapper}>
                    {content}
                </div>
            </div>
        </ConfirmationModal>
    );
};
